import { integrationStudioApi } from 'app/services/api';
import { normalize, schema } from 'normalizr';

import { catchAsyncActionError } from 'utils/action';
import { getConditionalObject } from 'utils/data';

export const INTEGRATION_STUDIO_ACTION_TYPE = {
  GET_PROVIDERS_REQUEST: 'INTEGRATION_STUDIO/GET_PROVIDERS_REQUEST',
  GET_PROVIDERS_SUCCESS: 'INTEGRATION_STUDIO/GET_PROVIDERS_SUCCESS',
  GET_PROVIDERS_FAILURE: 'INTEGRATION_STUDIO/GET_PROVIDERS_FAILURE',

  UPDATE_PROVIDER: 'INTEGRATION_STUDIO/UPDATE_PROVIDER',

  GET_STATS_REQUEST: 'INTEGRATION_STUDIO/GET_STATS_REQUEST',
  GET_STATS_SUCCESS: 'INTEGRATION_STUDIO/GET_STATS_SUCCESS',
  GET_STATS_FAILURE: 'INTEGRATION_STUDIO/GET_STATS_FAILURE',

  GET_PACKAGES_REQUEST: 'INTEGRATION_STUDIO/GET_PACKAGES_REQUEST',
  GET_PACKAGES_SUCCESS: 'INTEGRATION_STUDIO/GET_PACKAGES_SUCCESS',
  GET_PACKAGES_FAILURE: 'INTEGRATION_STUDIO/GET_PACKAGES_FAILURE',

  GET_METHODS_REQUEST: 'INTEGRATION_STUDIO/GET_METHODS_REQUEST',
  GET_METHODS_SUCCESS: 'INTEGRATION_STUDIO/GET_METHODS_SUCCESS',
  GET_METHODS_FAILURE: 'INTEGRATION_STUDIO/GET_METHODS_FAILURE',

  GET_METHOD: 'INTEGRATION_STUDIO/GET_METHOD',

  CREATE_PACKAGE: 'INTEGRATION_STUDIO/CREATE_PACKAGE',
  UPDATE_PACKAGE: 'INTEGRATION_STUDIO/UPDATE_PACKAGE',
  DELETE_PACKAGE: 'INTEGRATION_STUDIO/DELETE_PACKAGE',

  CREATE_METHOD: 'INTEGRATION_STUDIO/CREATE_METHOD',
  UPDATE_METHOD: 'INTEGRATION_STUDIO/UPDATE_METHOD',
  DELETE_METHOD: 'INTEGRATION_STUDIO/DELETE_METHOD',

  GET_ALLOWED_HOSTS_SUCCESS: 'INTEGRATION_STUDIO/GET_ALLOWED_HOSTS_SUCCESS',

  GET_RUN_HISTORY_REQUEST: 'INTEGRATION_STUDIO/GET_RUN_HISTORY_REQUEST',
  GET_RUN_HISTORY_SUCCESS: 'INTEGRATION_STUDIO/GET_RUN_HISTORY_SUCCESS',
  GET_RUN_HISTORY_FAILURE: 'INTEGRATION_STUDIO/GET_RUN_HISTORY_FAILURE',

  GET_METHOD_RUN_HISTORY_REQUEST: 'INTEGRATION_STUDIO/GET_METHOD_RUN_HISTORY_REQUEST',
  GET_METHOD_RUN_HISTORY_SUCCESS: 'INTEGRATION_STUDIO/GET_METHOD_RUN_HISTORY_SUCCESS',
  GET_METHOD_RUN_HISTORY_FAILURE: 'INTEGRATION_STUDIO/GET_METHOD_RUN_HISTORY_FAILURE',

  GET_MAPPINGS_REQUEST: 'INTEGRATION_STUDIO/GET_MAPPINGS_REQUEST',
  GET_MAPPINGS_SUCCESS: 'INTEGRATION_STUDIO/GET_MAPPINGS_SUCCESS',
  GET_MAPPINGS_FAILURE: 'INTEGRATION_STUDIO/GET_MAPPINGS_FAILURE',

  SAVE_MAPPINGS: 'INTEGRATION_STUDIO/SAVE_MAPPINGS',
};

const methodSchema = new schema.Entity('methods', {}, { idAttribute: 'id' });
const packageSchema = new schema.Entity(
  'packages',
  { methods: [methodSchema] },
  { idAttribute: 'id' },
);
const packagesListSchema = [packageSchema];
const methodsListSchema = [methodSchema];

// A caller outside the Integration Studio admin page (e.g. the disclosures wizard probing whether
// physical mail is enabled) may not hold `integrations.view` — pass `skipResponseInterceptor` so a
// 403 there degrades quietly instead of triggering the global forbidden-error page reload.
export const getProvidersAction = catchAsyncActionError(
  ({ skipResponseInterceptor } = {}) =>
    async dispatch => {
      dispatch({ type: INTEGRATION_STUDIO_ACTION_TYPE.GET_PROVIDERS_REQUEST });

      const response = await integrationStudioApi.getProviders({ skipResponseInterceptor });

      dispatch({
        type: INTEGRATION_STUDIO_ACTION_TYPE.GET_PROVIDERS_SUCCESS,
        payload: response.data ?? [],
      });

      return response.data;
    },
);

export const getProviderConfigAction = catchAsyncActionError(
  ({ providerKey }) =>
    async dispatch => {
      const response = await integrationStudioApi.getProviderConfig(providerKey);

      dispatch({
        type: INTEGRATION_STUDIO_ACTION_TYPE.UPDATE_PROVIDER,
        payload: { providerKey, patch: response.data },
      });

      return response.data;
    },
);

export const updateProviderConfigAction = catchAsyncActionError(
  ({ providerKey, settings }) =>
    async dispatch => {
      const response = await integrationStudioApi.updateProviderConfig(providerKey, settings);

      dispatch({
        type: INTEGRATION_STUDIO_ACTION_TYPE.UPDATE_PROVIDER,
        payload: { providerKey, patch: response.data },
      });

      return response.data;
    },
);

// The credential response is a FLAT credential record (not a provider config), so unlike the
// config GET/PUT it cannot be patched wholesale. Project the two provider-level facts it establishes:
// the credential now exists, and the config warnings the backend recomputed after the write (e.g. a
// stored shared secret with no header name — the guard would be silently unenforced).
//
// `warnings` is patched ONLY when the response actually carries it. Defaulting a missing field to []
// would let this path DELETE a fail-open security warning without server confirmation — and it is the
// one path that can create that state: the vault write succeeds, the settings PUT then fails (which
// deliberately skips the config refetch), so the secret is stored with no header name and the notice
// that should be screaming has been blanked by us.
export const setProviderCredentialAction = catchAsyncActionError(
  ({ providerKey, credentialKey, value }) =>
    async dispatch => {
      const response = await integrationStudioApi.setProviderCredential(
        providerKey,
        credentialKey,
        value,
      );

      dispatch({
        type: INTEGRATION_STUDIO_ACTION_TYPE.UPDATE_PROVIDER,
        payload: {
          providerKey,
          patch: {
            hasCredential: true,
            ...getConditionalObject(!!response.data?.warnings, () => ({
              warnings: response.data.warnings,
            })),
          },
        },
      });

      return response.data;
    },
);

export const enableProviderAction = catchAsyncActionError(({ providerKey }) => async dispatch => {
  await integrationStudioApi.enableProvider(providerKey);

  dispatch({
    type: INTEGRATION_STUDIO_ACTION_TYPE.UPDATE_PROVIDER,
    payload: { providerKey, patch: { isEnabled: true } },
  });
});

export const disableProviderAction = catchAsyncActionError(({ providerKey }) => async dispatch => {
  await integrationStudioApi.disableProvider(providerKey);

  dispatch({
    type: INTEGRATION_STUDIO_ACTION_TYPE.UPDATE_PROVIDER,
    payload: { providerKey, patch: { isEnabled: false } },
  });
});

export const getStatsAction = catchAsyncActionError(() => async dispatch => {
  dispatch({ type: INTEGRATION_STUDIO_ACTION_TYPE.GET_STATS_REQUEST });

  const response = await integrationStudioApi.getStats();

  dispatch({
    type: INTEGRATION_STUDIO_ACTION_TYPE.GET_STATS_SUCCESS,
    payload: response.data,
  });

  return response.data;
});

export const getPackagesAction = catchAsyncActionError(() => async dispatch => {
  dispatch({ type: INTEGRATION_STUDIO_ACTION_TYPE.GET_PACKAGES_REQUEST });

  const response = await integrationStudioApi.getPackages();
  const normalized = normalize(response.data, packagesListSchema);

  dispatch({
    type: INTEGRATION_STUDIO_ACTION_TYPE.GET_PACKAGES_SUCCESS,
    payload: {
      packages: normalized.entities.packages || {},
      methods: normalized.entities.methods || {},
    },
  });
});

// The list endpoint returns a summary DTO (no baseUrlDev / baseUrlStaging / baseUrlProd /
// sharedAuthJson), so the package editor must hydrate from the single-package endpoint, which
// returns the full record — otherwise the per-environment base URLs never round-trip into the
// settings form and a save would clear them.
export const getPackageAction = catchAsyncActionError(({ packageId }) => async dispatch => {
  const response = await integrationStudioApi.getPackage(packageId);
  const normalized = normalize(response.data, packageSchema);

  dispatch({
    type: INTEGRATION_STUDIO_ACTION_TYPE.UPDATE_PACKAGE,
    payload: normalized.entities.packages,
  });

  return response.data;
});

export const getMethodsAction = catchAsyncActionError(({ packageId }) => async dispatch => {
  dispatch({ type: INTEGRATION_STUDIO_ACTION_TYPE.GET_METHODS_REQUEST });

  const response = await integrationStudioApi.getMethods(packageId);
  const normalized = normalize(response.data, methodsListSchema);

  dispatch({
    type: INTEGRATION_STUDIO_ACTION_TYPE.GET_METHODS_SUCCESS,
    payload: normalized.entities.methods || {},
  });
});

// The list endpoint returns a summary DTO (no headersJson / timeoutSeconds / notes),
// so the method editor must hydrate from the single-method endpoint, which returns the
// full record — otherwise headers and timeout never round-trip into the edit form.
export const getMethodAction = catchAsyncActionError(
  ({ packageId, methodId }) =>
    async dispatch => {
      const response = await integrationStudioApi.getMethod(packageId, methodId);
      const normalized = normalize(response.data, methodSchema);

      dispatch({
        type: INTEGRATION_STUDIO_ACTION_TYPE.GET_METHOD,
        payload: normalized.entities.methods || {},
      });

      return response.data;
    },
);

export const createPackageAction = catchAsyncActionError(data => async dispatch => {
  const response = await integrationStudioApi.createPackage(data);
  const normalized = normalize(response.data, packageSchema);

  dispatch({
    type: INTEGRATION_STUDIO_ACTION_TYPE.CREATE_PACKAGE,
    payload: normalized.entities.packages,
  });

  return response.data;
});

export const updatePackageAction = catchAsyncActionError(
  ({ packageId, ...data }) =>
    async dispatch => {
      const response = await integrationStudioApi.updatePackage(packageId, data);
      const normalized = normalize(response.data, packageSchema);

      dispatch({
        type: INTEGRATION_STUDIO_ACTION_TYPE.UPDATE_PACKAGE,
        payload: normalized.entities.packages,
      });

      return response.data;
    },
);

export const deletePackageAction = catchAsyncActionError(({ packageId }) => async dispatch => {
  await integrationStudioApi.deletePackage(packageId);

  dispatch({
    type: INTEGRATION_STUDIO_ACTION_TYPE.DELETE_PACKAGE,
    meta: { packageId },
  });
});

export const createMethodAction = catchAsyncActionError(
  ({ packageId, ...data }) =>
    async dispatch => {
      const response = await integrationStudioApi.createMethod(packageId, data);
      const normalized = normalize(response.data, methodSchema);

      dispatch({
        type: INTEGRATION_STUDIO_ACTION_TYPE.CREATE_METHOD,
        payload: normalized.entities.methods,
      });

      return response.data;
    },
);

export const updateMethodAction = catchAsyncActionError(
  ({ packageId, methodId, ...data }) =>
    async dispatch => {
      const response = await integrationStudioApi.updateMethod(packageId, methodId, data);
      const normalized = normalize(response.data, methodSchema);

      dispatch({
        type: INTEGRATION_STUDIO_ACTION_TYPE.UPDATE_METHOD,
        payload: normalized.entities.methods,
      });

      return response.data;
    },
);

export const deleteMethodAction = catchAsyncActionError(
  ({ packageId, methodId }) =>
    async dispatch => {
      await integrationStudioApi.deleteMethod(packageId, methodId);

      dispatch({
        type: INTEGRATION_STUDIO_ACTION_TYPE.DELETE_METHOD,
        meta: { methodId },
      });
    },
);

export const getMappingsAction = catchAsyncActionError(
  ({ packageId, methodId }) =>
    async dispatch => {
      dispatch({ type: INTEGRATION_STUDIO_ACTION_TYPE.GET_MAPPINGS_REQUEST, meta: { methodId } });

      const response = await integrationStudioApi.getMappings(packageId, methodId);

      dispatch({
        type: INTEGRATION_STUDIO_ACTION_TYPE.GET_MAPPINGS_SUCCESS,
        payload: response.data,
        meta: { methodId },
      });

      return response.data;
    },
);

export const saveMappingsAction = catchAsyncActionError(
  ({ packageId, methodId, requestMappingSpecJson, responseMappings }) =>
    async dispatch => {
      const response = await integrationStudioApi.saveMappings(packageId, methodId, {
        requestMappingSpecJson,
        responseMappings,
      });

      dispatch({
        type: INTEGRATION_STUDIO_ACTION_TYPE.SAVE_MAPPINGS,
        payload: response.data,
        meta: { methodId },
      });

      return response.data;
    },
);

// Constants have no store slice — the ConstantsConfigSection holds them in local state (like the
// credential/allowed-host surfaces), so these actions only wrap the API call for centralized error
// handling (catchAsyncActionError) and return the data to the caller.
export const getConstantsAction = catchAsyncActionError(({ packageId, config }) => async () => {
  const response = await integrationStudioApi.getConstants(packageId, config);

  return response.data ?? [];
});

export const setConstantAction = catchAsyncActionError(({ packageId, constant }) => async () => {
  const response = await integrationStudioApi.setConstant(packageId, constant);

  return response.data;
});

export const deleteConstantAction = catchAsyncActionError(
  ({ packageId, constantId }) =>
    async () => {
      await integrationStudioApi.deleteConstant(packageId, constantId);
    },
);

export const rotateConstantAction = catchAsyncActionError(
  ({ packageId, constantId, value }) =>
    async () => {
      const response = await integrationStudioApi.rotateConstant(packageId, constantId, value);

      return response.data;
    },
);

export const getAllowedHostsAction = catchAsyncActionError(({ packageId }) => async dispatch => {
  const response = await integrationStudioApi.getAllowedHosts(packageId);

  dispatch({
    type: INTEGRATION_STUDIO_ACTION_TYPE.GET_ALLOWED_HOSTS_SUCCESS,
    payload: response.data ?? [],
    meta: { packageId },
  });

  return response.data;
});

export const getRunHistoryAction = catchAsyncActionError((params = {}) => async dispatch => {
  dispatch({ type: INTEGRATION_STUDIO_ACTION_TYPE.GET_RUN_HISTORY_REQUEST });

  const response = await integrationStudioApi.getRunHistory(params);

  dispatch({
    type: INTEGRATION_STUDIO_ACTION_TYPE.GET_RUN_HISTORY_SUCCESS,
    payload: response.data,
  });
});

export const getMethodRunHistoryAction = catchAsyncActionError(
  ({ packageId, methodId, ...params }) =>
    async dispatch => {
      dispatch({
        type: INTEGRATION_STUDIO_ACTION_TYPE.GET_METHOD_RUN_HISTORY_REQUEST,
        meta: { methodId },
      });

      try {
        const response = await integrationStudioApi.getRunHistoryForMethod(
          packageId,
          methodId,
          params,
        );

        dispatch({
          type: INTEGRATION_STUDIO_ACTION_TYPE.GET_METHOD_RUN_HISTORY_SUCCESS,
          payload: response.data,
          meta: { methodId },
        });
      } catch (error) {
        // Clear the per-method loading flag so a failed fetch shows an empty panel
        // rather than a perpetual skeleton; re-throw so catchAsyncActionError still toasts.
        dispatch({
          type: INTEGRATION_STUDIO_ACTION_TYPE.GET_METHOD_RUN_HISTORY_FAILURE,
          payload: error,
          meta: { methodId },
        });

        throw error;
      }
    },
);
